import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'

export interface TourStep {
  id: string
  target: string // CSS selector or data-tour attribute
  title: string
  description: string
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center'
  mobileOnly?: boolean
  desktopOnly?: boolean
}

const ONBOARDING_STEPS: TourStep[] = [
  {
    id: 'welcome',
    target: '[data-tour="conversation-area"]',
    title: '👋 Welcome to Sage!',
    description: 'Your AI companion is here to help. Let\'s take a quick tour of the key features.',
    placement: 'center'
  },
  {
    id: 'voice-mode',
    target: '[data-tour="voice-button"]',
    title: '🎙️ Voice Mode',
    description: 'Tap here to have natural voice conversations with your AI companion. It\'s fast, responsive, and sounds incredibly natural!',
    placement: 'bottom'
  },
  {
    id: 'voice-settings',
    target: '[data-tour="voice-settings"]',
    title: '⚙️ Voice Settings',
    description: 'Customize your voice experience here. Choose from different AI voices and adjust settings to your preference.',
    placement: 'bottom'
  },
  {
    id: 'attach-image',
    target: '[data-tour="attach-image"]',
    title: '📷 Share Images',
    description: 'Attach images to your conversations. Your AI can analyze photos, help identify objects, and more!',
    placement: 'top'
  },
  {
    id: 'follow-up-options',
    target: '[data-tour="follow-up-options"]',
    title: '💬 Smart Follow-ups',
    description: 'After each response, you\'ll see helpful follow-up suggestions. Tap any option for instant, contextual responses.',
    placement: 'top'
  },
  {
    id: 'category-explore-desktop',
    target: '[data-tour="category-bar"]',
    title: '👋 Explore Categories',
    description: 'Click and drag this bar horizontally to discover all the specialized wisdom categories Sage offers!',
    placement: 'bottom',
    desktopOnly: true
  },
  {
    id: 'category-explore-mobile',
    target: '[data-tour="category-toggle"]',
    title: '👋 Explore Categories',
    description: 'Tap this icon to reveal all of Sage\'s wisdom categories in a quick-access menu!',
    placement: 'bottom',
    mobileOnly: true
  },
  {
    id: 'category-customize',
    target: '[data-tour="category-bar"]',
    title: '✨ Customize Your View',
    description: 'Hover over a category to hide it. You can manage and restore your hidden categories anytime to keep your workspace clean.',
    placement: 'bottom',
    desktopOnly: true
  },
  {
    id: 'dashboard',
    target: '[data-tour="dashboard"]',
    title: '🏠 Dashboard',
    description: 'Access your dashboard here to view conversation history, manage settings, and explore different conversation categories.',
    placement: 'bottom',
    mobileOnly: true
  },
  {
    id: 'quiz',
    target: '[data-tour="quiz-button"]',
    title: '🎯 Interactive Quizzes',
    description: 'Test your knowledge with interactive quizzes. Perfect for learning and reinforcing new concepts!',
    placement: 'top'
  },
  {
    id: 'complete',
    target: '[data-tour="conversation-area"]',
    title: '🎉 You\'re All Set!',
    description: 'You\'re ready to start! Remember, you can always click the "?" icon in the top right to replay this tour.',
    placement: 'center'
  }
]

export function useOnboarding() {
  const { user } = useAuth()
  const [showTour, setShowTour] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [hasCompletedTour, setHasCompletedTour] = useState(false)

  // Check if user has completed onboarding
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // Prefer local flag to avoid UI refresh loops if auth state updates
      try {
        if (typeof window !== 'undefined') {
          const localCompleted = window.localStorage.getItem('onboarding_completed') === 'true'
          if (localCompleted) {
            setHasCompletedTour(true)
            setShowTour(false)
            setIsLoading(false)
            return
          }
        }
      } catch {
        // Ignore localStorage errors (private mode, etc.)
      }

      if (!user) {
        setIsLoading(false)
        return
      }

      try {
        // Check user metadata for onboarding completion
        const metadata = user.user_metadata as Record<string, unknown> | null
        const completed = metadata?.onboarding_completed === true

        console.log('[ONBOARDING] User metadata check:', { completed, metadata })
        setHasCompletedTour(completed)

        // Show tour for new users who haven't completed it
        if (!completed) {
          // Small delay for better UX
          setTimeout(() => {
            console.log('[ONBOARDING] 🎯 Starting tour for new user')
            setShowTour(true)
          }, 1500)
        }
      } catch (error) {
        console.error('[ONBOARDING] Error checking status:', error)
      } finally {
        setIsLoading(false)
      }
    }

    checkOnboardingStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]) // Only depend on user ID, not the whole user object

  // Get filtered steps based on device
  const getActiveSteps = useCallback((): TourStep[] => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768

    return ONBOARDING_STEPS.filter(step => {
      if (step.mobileOnly && !isMobile) return false
      if (step.desktopOnly && isMobile) return false
      return true
    })
  }, [])

  const activeSteps = getActiveSteps()

  const completeTour = useCallback(async () => {
    console.log('[ONBOARDING] ✅ Completing tour...')

    // Immediately hide tour and update state
    setShowTour(false)
    setCurrentStep(0)
    setHasCompletedTour(true)

    // Persist locally immediately to prevent re-showing on any remount
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('onboarding_completed', 'true')
      }
    } catch {
      // Ignore localStorage failures
    }

    if (!user) {
      console.log('[ONBOARDING] No user, skipping save')
      return
    }

    // Save asynchronously without blocking UI
    // Save asynchronously without blocking UI and without awaiting in callers
    setTimeout(async () => {
      try {
        await supabase.auth.updateUser({
          data: {
            onboarding_completed: true,
            onboarding_completed_at: new Date().toISOString()
          }
        })
        console.log('[ONBOARDING] ✅ Tour completion saved to user metadata')
      } catch (error) {
        console.error('[ONBOARDING] ❌ Error saving completion:', error)
      }
    }, 100)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]) // Only depend on user ID

  const nextStep = useCallback(() => {
    if (currentStep < activeSteps.length - 1) {
      setCurrentStep(prev => prev + 1)
    } else {
      completeTour()
    }
  }, [currentStep, activeSteps.length, completeTour])

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }, [currentStep])

  const skipTour = useCallback(() => {
    setShowTour(false)
    setCurrentStep(0)
    // Do not await; let completion persist asynchronously to avoid any reload flicker
    void completeTour()
  }, [completeTour])

  const restartTour = useCallback(() => {
    setCurrentStep(0)
    setShowTour(true)
  }, [])

  return {
    showTour,
    currentStep,
    activeSteps,
    totalSteps: activeSteps.length,
    isLoading,
    hasCompletedTour,
    nextStep,
    previousStep,
    skipTour,
    restartTour,
    currentStepData: activeSteps[currentStep]
  }
}

